import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

// Styled hover/focus tooltip shared across the app. The bubble is rendered in a
// portal on <body> as position:fixed, so it always paints in front and is never
// clipped by an ancestor's overflow or trapped in a stacking context — wherever
// the trigger sits. Positioned in JS from the trigger's rect (re-run on
// scroll/resize). The trigger stays keyboard-reachable and exposes the bubble
// via aria-describedby. Dark ink bubble with cream text + a small arrow.

type Placement = "top" | "bottom" | "left" | "right";

const GAP = 8; // px between trigger and bubble (matches the old mb-2/mt-2)
const MARGIN = 8; // min gap from the viewport edge when clamping

const ARROW_POSITION: Record<Placement, string> = {
  top: "left-1/2 top-full -translate-x-1/2 -translate-y-1/2",
  bottom: "left-1/2 bottom-full -translate-x-1/2 translate-y-1/2",
  left: "top-1/2 left-full -translate-x-1/2 -translate-y-1/2",
  right: "top-1/2 right-full translate-x-1/2 -translate-y-1/2",
};

export function Tooltip({
  content,
  placement = "top",
  className = "",
  contentClassName = "",
  interactive = false,
  children,
}: {
  content: ReactNode;
  placement?: Placement;
  className?: string;
  // Overrides the bubble width/box classes (default: max-w-[16rem]) for
  // content-rich tooltips that would otherwise clip.
  contentClassName?: string;
  // Keeps the OPEN bubble hoverable (scroll, text selection). While hidden it
  // is not rendered at all, so it can never block clicks near the trigger.
  interactive?: boolean;
  children: ReactNode;
}) {
  const id = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);

  const show = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  }, []);
  // Interactive tooltips get a short grace period so the pointer can travel
  // from the trigger to the bubble without it closing underneath.
  const hide = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), interactive ? 80 : 0);
  }, [interactive]);

  // Position the portaled bubble from the trigger's rect. Written straight to
  // the node (no state → no extra render), and re-run while open on scroll/resize.
  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      const bubble = bubbleRef.current;
      const t = triggerRef.current?.getBoundingClientRect();
      const b = bubble?.getBoundingClientRect();
      if (!bubble || !t || !b) return;
      const cx = t.left + t.width / 2;
      const cy = t.top + t.height / 2;
      let top = 0;
      let left = 0;
      switch (placement) {
        case "top":
          top = t.top - GAP - b.height;
          left = cx - b.width / 2;
          break;
        case "bottom":
          top = t.bottom + GAP;
          left = cx - b.width / 2;
          break;
        case "left":
          top = cy - b.height / 2;
          left = t.left - GAP - b.width;
          break;
        case "right":
          top = cy - b.height / 2;
          left = t.right + GAP;
          break;
      }
      // Keep the bubble inside the viewport.
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - b.width - MARGIN));
      top = Math.max(MARGIN, Math.min(top, window.innerHeight - b.height - MARGIN));
      bubble.style.top = `${top}px`;
      bubble.style.left = `${left}px`;
      bubble.style.opacity = "1";
    };
    position();
    window.addEventListener("scroll", position, true);
    window.addEventListener("resize", position);
    return () => {
      window.removeEventListener("scroll", position, true);
      window.removeEventListener("resize", position);
    };
  }, [open, placement, content]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  return (
    <span
      ref={triggerRef}
      className={`relative inline-flex ${className}`}
      tabIndex={0}
      aria-describedby={open ? id : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <span
            ref={bubbleRef}
            role="tooltip"
            id={id}
            onMouseEnter={interactive ? show : undefined}
            onMouseLeave={interactive ? hide : undefined}
            style={{ position: "fixed", top: -9999, left: -9999, opacity: 0, zIndex: 9999 }}
            className={`${interactive ? "" : "pointer-events-none"} w-max rounded-xl bg-ink px-3 py-2 text-left text-[11px] font-normal normal-case leading-snug tracking-normal text-on-ink shadow-xl transition-opacity duration-150 ${contentClassName || "max-w-[16rem]"}`}
          >
            {content}
            <span className={`absolute h-2 w-2 rotate-45 bg-ink ${ARROW_POSITION[placement]}`} aria-hidden />
          </span>,
          document.body,
        )}
    </span>
  );
}
